<template>
  <!-- Page Content -->
  <div class="whole-page" id="whole-page-div">
    <AIChatFloat :colorTheme="colorTheme" />
    <div class="navbar-div">
      <NavbarComponent
        :user="user"
        :colorTheme="colorTheme"
        analysis="ADVANCED"
        :selectedNavLink="selectedNavLink"
        @eventOnUserSelectNavLink="eventOnClientSelect"
        @eventOnUserColorTheme="onUserColorTheme"
      />
    </div>
    <div class="h-100 d-flex flex-column">
      <!-- Link 0 selected / Currency -->
      <div class="row f-row h-100" v-if="selectedNavLink === 0">
        <div class="col col-12 h-100">
          <Currency
            :key="advancedRouteKey"
            :analytics_preferences="analytics_preferences"
            :colorTheme="colorTheme"
            :companies="companies"
            :countrySpecs="countrySpecs"
            :currencySpecs="currencySpecs"
            :processData="processData"
            :routePickedAnalysis="routePickedAnalysis"
          />
        </div>
      </div>
      <!-- Link 1 selected / Commodity -->
      <div class="row f-row h-100" v-if="selectedNavLink === 1">
        <div class="col col-12 h-100">
          <Commodity
            :key="advancedRouteKey"
            :analytics_preferences="analytics_preferences"
            :colorTheme="colorTheme"
            :companies="companies"
            :countrySpecs="countrySpecs"
            :processData="processData"
            :routePickedAnalysis="routePickedAnalysis"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIChatFloat from "@/components/ai/AIChatFloat.vue";
import Commodity from "@/components/advanced/Commodity.vue";
import Currency from "@/components/advanced/Currency";
import NavbarComponent from "@/components/Navbar.vue";
import {
  assetSelectionVuex,
  colorThemeChange,
  colorThemeVuex,
  sortByKey,
} from "@/_core/_helpers/_helpers";
import { ProcessData } from "@/_core/data/ProcessData";
import { saveUserPreferences } from "@/_core/data/SaveToDB";
import { getDefaultAnalyticsPreferences } from "@/_core/data/Advanced";
import { getCompanyParameters } from "@/_core/data/Overview";

export default {
  name: "Advanced",
  components: {
    AIChatFloat,
    Commodity,
    Currency,
    NavbarComponent,
  },
  data() {
    return {
      analytics_preferences: {},
      routePickedAnalysis: null,
      colorTheme: true, // true = dark, else light
      companies: [{ id: 0, company_name: "N.A." }],
      countrySpecs: [],
      currencySpecs: [],
      linkList: ["Currencies", "Commodities"],
      processData: null,
      root: document.documentElement,
      selectedNavLink: 0,
      user: [],
    };
  },
  computed: {
    advancedRouteKey() {
      return this.$route.params.productType || "default";
    },
  },
  watch: {
    "$route.params.productType": {
      immediate: true,
      handler(newProductType) {
        this.applyRouteProductType(newProductType);
      },
    },
    "$route.name": {
      immediate: true,
      handler() {
        this.applyRouteAnalysisType();
      },
    },
    "$route.query": {
      immediate: true,
      deep: true,
      handler() {
        this.applyRouteQuerySelections();
      },
    },
  },
  created() {
    this.analytics_preferences = getDefaultAnalyticsPreferences(this)[
      "choices"
    ];
    try {
      this.colorTheme = this.analytics_preferences.color_theme;
      if (this.colorTheme === undefined) {
        this.colorTheme = true;
      }
      if (!this.colorTheme) {
        colorThemeChange(this.root, this.colorTheme);
        this.onUserColorTheme(this.colorTheme);
      }
    } catch (error) {
      console.log(`Error color theme ${error}`);
    }
    try {
      this.selectedNavLink = this.analytics_preferences.selected_nav;
      if (this.selectedNavLink === undefined || this.selectedNavLink == null) {
        this.selectedNavLink = 0;
      }
    } catch (error) {
      this.selectedNavLink = 0;
      console.log(`Error selected nav link ${error}`);
    }
    try {
      this.companies = this.$store.state.companies.all.items;
      this.companies = sortByKey(this.companies, "company_name");
      this.companies.forEach(function(d, i) {
        d.id = i;
      });
      let auxBool = false;
      const storePrefs = this.$store.getters[
        "user_preferences/analyticsChoices"
      ];
      if (
        storePrefs?.selected_company === undefined ||
        storePrefs?.selected_company == null
      ) {
        auxBool = true;
      } else if (storePrefs?.selected_company.length === 0) {
        auxBool = true;
      }
      if (auxBool) {
        this.$store.dispatch("user_preferences/updateAnalyticsChoices", {
          ...this.analytics_preferences,
          selected_company: [this.companies[0]],
        });
        this.saveUserPref();
      }
    } catch (error) {
      console.log(`Error companies ${error}`);
    }
    this.processData = new ProcessData();
    try {
      this.user = this.$store.state.user.all.items;
    } catch (error) {
      console.log(`Error user ${error}`);
    }
    try {
      this.countrySpecs = this.$store.state.countries.all.items;
    } catch (error) {
      console.log(`Error countries ${error}`);
    }
    try {
      this.currencySpecs = this.$store.state.currencies.all.items;
    } catch (error) {
      console.log(`Error currencies ${error}`);
    }

    // Apply route-driven defaults after base data is ready.
    this.applyRouteProductType(this.$route.params.productType);
    this.applyRouteAnalysisType();
    this.applyRouteQuerySelections();
  },
  mounted() {
    this.getCompParams();
  },
  methods: {
    normalizeProductType(productType) {
      if (!productType) return null;
      const v = String(productType).toLowerCase();
      if (v === "commodity" || v === "commodities") return "commodity";
      if (v === "currency" || v === "currencies") return "currency";
      return v;
    },
    parseRoute(val) {
      if (val == null) return [];
      if (Array.isArray(val)) return val.flatMap((v) => String(v).split(","));
      return String(val)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    applyRouteProductType(productType) {
      const normalized = this.normalizeProductType(productType);
      if (!normalized) return;
      const nextNav = normalized === "commodity" ? 1 : 0;
      if (this.selectedNavLink === nextNav) return;
      this.eventOnClientSelect(nextNav);
    },
    applyRouteAnalysisType() {
      if (this.$route.name === "advanced-historical-analysis") {
        this.routePickedAnalysis = 1;
      } else if (this.$route.name === "advanced-scenario-analysis") {
        this.routePickedAnalysis = 2;
      } else {
        this.routePickedAnalysis = null;
      }
    },
    applyRouteQuerySelections() {
      const q = this.$route.query || {};
      const normalized = this.normalizeProductType(
        this.$route.params.productType,
      );
      const assetType =
        normalized || (this.selectedNavLink === 1 ? "commodity" : "currency");

      // companies
      const companyTokens = this.parseRoute(q.companies);
      if (companyTokens.length > 0 && Array.isArray(this.companies)) {
        const selectedCompanies = this.companies.filter((c) => {
          const idStr = c?.id != null ? String(c.id) : "";
          const nameStr = (c?.company_name || "").toLowerCase();
          return companyTokens.some((t) => {
            const tt = String(t).trim();
            if (!tt) return false;
            return tt === idStr || nameStr === tt.toLowerCase();
          });
        });
        if (selectedCompanies.length > 0) {
          this.analytics_preferences.selected_company = selectedCompanies;
        }
      }

      // portfolios (try to match by pk or by name)
      const portfolioTokens = this.parseRoute(q.portfolios);
      if (
        portfolioTokens.length > 0 &&
        Array.isArray(this.analytics_preferences.selected_company)
      ) {
        const portfolios = [];
        this.analytics_preferences.selected_company.forEach((c) => {
          (c?.portfolios || []).forEach((p) => portfolios.push(p));
        });
        const selectedPortfolios = portfolios.filter((p) => {
          const pkStr =
            p?.pk != null
              ? String(p.pk)
              : p?.pk_idportfolio != null
              ? String(p.pk_idportfolio)
              : "";
          const nameStr = (p?.portfolio_name || p?.name || "").toLowerCase();
          return portfolioTokens.some((t) => {
            const tt = String(t).trim();
            if (!tt) return false;
            return tt === pkStr || nameStr === tt.toLowerCase();
          });
        });
        if (selectedPortfolios.length > 0) {
          this.analytics_preferences.selected_portfolio = selectedPortfolios;
        }
      }

      // products
      if (assetType === "currency") {
        const currencyTokens = this.parseRoute(q.currencies);
        if (currencyTokens.length > 0 && Array.isArray(this.currencySpecs)) {
          const selectedCurrencies = this.currencySpecs.filter((c) => {
            const pkStr =
              c?.pk_i_idcurrency != null ? String(c.pk_i_idcurrency) : "";
            const nameStr = (c?.currency_name || "").toLowerCase();
            return currencyTokens.some((t) => {
              const tt = String(t).trim();
              if (!tt) return false;
              return tt === pkStr || nameStr === tt.toLowerCase();
            });
          });
          if (selectedCurrencies.length > 0) {
            this.analytics_preferences.selected_currencies = selectedCurrencies;
          }
        }
      } else if (assetType === "commodity") {
        const commodityTokens = this.parseRoute(q.commodities);
        if (commodityTokens.length > 0) {
          this.analytics_preferences.selected_commodities = commodityTokens;

          const endProducts = this.$store.state.companyParameters?.endProducts;
          if (Array.isArray(endProducts)) {
            const selectedAssets = endProducts.filter((p) => {
              const pkStr =
                p?.pk_id_end_product != null ? String(p.pk_id_end_product) : "";
              const nameStr = (p?.product_name || "").toLowerCase();
              return commodityTokens.some((t) => {
                const tt = String(t).trim();
                if (!tt) return false;
                return tt === pkStr || nameStr === tt.toLowerCase();
              });
            });
            if (selectedAssets.length > 0) {
              this.analytics_preferences.selectedAsset = selectedAssets;
              this.analytics_preferences.selected_commodities = selectedAssets;
            }
          }
        }
      }
    },
    async getCompParams() {
      let compParam = await getCompanyParameters(this.companies);
      Object.assign(this.$store.state, {
        companyParameters: compParam,
      });
    },
    onUserColorTheme(val) {
      this.colorTheme = val;
      this.analytics_preferences.color_theme = val;
      colorThemeVuex(this, this.colorTheme);
      saveUserPreferences(this);
    },
    eventOnClientSelect(idx) {
      this.analytics_preferences.selected_nav = idx;
      assetSelectionVuex(this, idx);

      this.saveUserPref();
      this.selectedNavLink = idx;
    },
    saveUserPref() {
      this.$store.dispatch(
        "user_preferences/updateAnalyticsChoices",
        this.analytics_preferences,
      );
      saveUserPreferences(this);
    },
  },
};
</script>

<style scoped></style>
